import { useSyncExternalStore } from "react";
import type { VoiceStudyPrompt } from "#voice-study-card";
import type { VoiceStudySessionMessage } from "#voice-study-session";

/** A transcript line shown in the voice chat: what the app said or what was heard/typed. */
export type VoiceStudyMessage = VoiceStudySessionMessage & { id: number; kind?: "error" };

const MAX_MESSAGES = 60;

/** Everything that lives exactly as long as one voice session (from start to stop). */
export type VoiceStudySessionState = {
  /** The emulated-speech input box's text; survives the pill remounting within the session. */
  readonly draft: string;
  /** Touch lock (the full-screen modal); a pause or a volume change releases it. */
  readonly locked: boolean;
  readonly messages: readonly VoiceStudyMessage[];
  readonly paused: boolean;
};

const INITIAL_STATE: VoiceStudySessionState = {
  draft: "",
  locked: false,
  messages: [],
  paused: false,
};

/** What a session needs from its controller; every operation is a no-op once the session ended. */
export type VoiceStudySessionHandleDeps = {
  hear: (text: string) => void;
  pause: () => void;
  replay: () => void;
  resume: () => void;
  say: (prompt: VoiceStudyPrompt) => Promise<void>;
  stop: () => void;
};

/**
 * One voice session: created when the session becomes active and dropped when it stops, so its
 * identity is stable for the session's whole lifetime and a new session starts from a clean
 * transcript. Holding one is proof that a session exists (or existed — calls on an ended session
 * do nothing). Fast-changing state (messages, paused, locked, draft) is NOT part of its identity:
 * read it with the `useVoiceStudySession*` hooks, which re-render only their caller.
 */
export class VoiceStudySessionHandle {
  private state: VoiceStudySessionState = INITIAL_STATE;
  private readonly listeners = new Set<() => void>();
  private messageId = 0;

  constructor(private readonly deps: VoiceStudySessionHandleDeps) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  readonly getState = (): VoiceStudySessionState => this.state;

  private update(patch: Partial<VoiceStudySessionState>): void {
    const next = { ...this.state, ...patch };
    if (
      next.draft === this.state.draft &&
      next.locked === this.state.locked &&
      next.messages === this.state.messages &&
      next.paused === this.state.paused
    ) {
      return;
    }
    this.state = next;
    for (const listener of this.listeners) {
      listener();
    }
  }

  appendMessage(message: VoiceStudySessionMessage & { kind?: "error" }): void {
    const current = this.state.messages;
    const last = current.at(-1);
    // A re-run of the same turn (mute toggle, focus churn) must not stutter the app's line.
    if (
      last !== undefined &&
      last.role === "app" &&
      message.role === "app" &&
      last.text === message.text
    ) {
      return;
    }
    this.messageId += 1;
    this.update({
      messages: [...current, { ...message, id: this.messageId }].slice(-MAX_MESSAGES),
    });
  }

  /** Speak once without changing the current turn; resolves when done or interrupted. */
  readonly say = (prompt: VoiceStudyPrompt): Promise<void> => this.deps.say(prompt);

  /** Feed text as if the recognizer heard it — the chat input for testing without a microphone. */
  readonly submitTranscript = (text: string): void => {
    this.deps.hear(text);
  };

  readonly setDraft = (draft: string): void => {
    this.update({ draft });
  };

  readonly setLocked = (locked: boolean): void => {
    this.update({ locked });
  };

  readonly pause = (): void => {
    this.update({ locked: false, paused: true });
    this.deps.pause();
  };

  readonly resume = (): void => {
    this.update({ locked: false, paused: false });
    this.deps.resume();
  };

  readonly togglePaused = (): void => {
    if (this.state.paused) {
      this.resume();
    } else {
      this.pause();
    }
  };

  /** Replay the current card's prompt (nothing while paused: resuming replays it anyway). */
  readonly repeat = (): void => {
    if (!this.state.paused) {
      this.deps.replay();
    }
  };

  readonly stop = (): void => {
    this.deps.stop();
  };
}

function useVoiceStudySessionSelector<T>(
  session: VoiceStudySessionHandle,
  select: (state: VoiceStudySessionState) => T,
): T {
  const getSnapshot = () => select(session.getState());
  return useSyncExternalStore(session.subscribe, getSnapshot, getSnapshot);
}

/** The session's chat transcript (≤ 60 lines); re-renders the caller per line. */
export function useVoiceStudySessionMessages(
  session: VoiceStudySessionHandle,
): readonly VoiceStudyMessage[] {
  return useVoiceStudySessionSelector(session, (state) => state.messages);
}

export function useVoiceStudySessionPaused(session: VoiceStudySessionHandle): boolean {
  return useVoiceStudySessionSelector(session, (state) => state.paused);
}

export function useVoiceStudySessionLocked(session: VoiceStudySessionHandle): boolean {
  return useVoiceStudySessionSelector(session, (state) => state.locked);
}

export function useVoiceStudySessionDraft(session: VoiceStudySessionHandle): string {
  return useVoiceStudySessionSelector(session, (state) => state.draft);
}
